import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from 'firebase/auth';
import { getUserProfile, updateUserProfile } from '../services/user/userProfileService';
import { saveUserProfile } from '../services/storage/localStorageService';
import { checkIsAdmin } from '../services/adminService';
import { initializeUserData } from '../services/userService';
import { updatePersistedUserData, getPersistedUserData } from '../services/auth/userPersistence';

interface AuthState {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  initialized: boolean;
  setUser: (user: User | null) => Promise<void>;
  setLoading: (loading: boolean) => void;
  updateUserProfile: (updates: Partial<User>) => Promise<void>;
  rehydrateUser: () => Promise<void>;
  clearState: () => void;
  checkAdminStatus: (userId: string) => Promise<void>;
  initializeUser: (user: User) => Promise<void>;
  setInitialized: (initialized: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      loading: true,
      isAdmin: false,
      initialized: false,
      
      setLoading: (loading: boolean) => set({ loading }),
      
      setInitialized: (initialized: boolean) => set({ initialized }),
      
      setUser: async (user) => {
        try {
          get().setLoading(true);
          
          if (user) {
            const profile = await getUserProfile(user.uid);
            const mergedUser = {
              ...user,
              ...profile
            };
            
            saveUserProfile(mergedUser);
            set({ user: mergedUser });
            
            // Check admin status for new user
            await get().checkAdminStatus(user.uid);
          } else {
            set({ user: null, isAdmin: false });
          }
        } finally {
          get().setLoading(false);
          get().setInitialized(true);
        }
      },

      updateUserProfile: async (updates) => {
        const currentUser = get().user;
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            ...updates,
          } as User;
          
          await updatePersistedUserData(currentUser.uid, updates);
          set({ user: updatedUser });
        }
      },

      rehydrateUser: async () => {
        try {
          get().setLoading(true);
          const currentUser = get().user;
          if (currentUser?.uid) {
            const persistedData = await getPersistedUserData(currentUser.uid);
            if (persistedData) {
              const rehydratedUser = {
                ...currentUser,
                displayName: persistedData.displayName || currentUser.displayName,
                photoURL: persistedData.photoURL || currentUser.photoURL,
                emailVerified: persistedData.emailVerified || currentUser.emailVerified,
              } as User;
              set({ user: rehydratedUser });
            }
          }
        } finally {
          get().setLoading(false);
        }
      },

      clearState: () => {
        set({ user: null, isAdmin: false, loading: false, initialized: false });
      },

      checkAdminStatus: async (userId: string) => {
        try {
          const isAdmin = await checkIsAdmin(userId);
          set({ isAdmin });
        } catch (error) {
          console.error('Error checking admin status:', error);
          set({ isAdmin: false });
        }
      },

      initializeUser: async (user: User) => {
        if (!user.email) {
          throw new Error('User email is required');
        }
        try {
          get().setLoading(true);
          await initializeUserData(user.uid, user.email);
          await get().rehydrateUser();
        } catch (error) {
          console.error('Error initializing user:', error);
          throw error;
        } finally {
          get().setLoading(false);
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user ? {
          uid: state.user.uid,
          email: state.user.email,
          displayName: state.user.displayName,
          photoURL: state.user.photoURL,
          emailVerified: state.user.emailVerified,
          metadata: state.user.metadata
        } : null,
        isAdmin: state.isAdmin,
        initialized: state.initialized
      })
    }
  )
);